import { createSelector } from 'reselect';
import { findAll, findAllValues } from '@/models/selectors';
import { List, Map, fromJS } from 'immutable';
import { IModels, IModelsObject } from '@/models/types';
import { getCurrentAccount, getCurrentUser } from '@/features/Auth/selectors';
import { IAccount } from '@/types/account';
import { ILocation } from '@/types/location';
import { IUser } from '@/types/user';
import { safeGetIn } from '@/utils/safeImmutable'; 

export const getTypedAccount = createSelector(
    getCurrentAccount,
    (ssoUser) => (ssoUser as unknown) as IAccount
);

export const getNumberOfTeamMembers = (state: Map<any, any>) => safeGetIn(state, ['settings', 'numberOfTeamMembers']);
export const getAccountStats = (state: Map<any, any>) => safeGetIn(state, ['settings', 'stats']);
export const getIsLoading = (state: Map<any, any>) => safeGetIn(state, ['settings', 'isLoading']);

export const getCurrentAccountMainLocation = createSelector(
  getTypedAccount,
  findAll('location'),
  (currentAccount: IAccount, locations: IModels) =>
    currentAccount && currentAccount.mainLocation && locations && locations[currentAccount.mainLocation],
);

export const getAccountInitialValues = createSelector(getTypedAccount, (currentAccount: IAccount) => {
  if (!currentAccount) return {};
  return {
    name: currentAccount.name,
    publishedEmailName: currentAccount.publishedEmail ? currentAccount.publishedEmail.slice(0, currentAccount.publishedEmail.indexOf('@')) : '',
    publishedEmail: currentAccount.publishedEmail,
    websiteUrl: currentAccount.websiteUrl,
    mainLocation: currentAccount.mainLocation,
    taxInvoiceNumber: currentAccount.taxInvoiceNumber,
    type: currentAccount.type,
  };
});

export const getAccountContactInitialValues = createSelector(
  getTypedAccount,
  getCurrentUser,
  (currentAccount: IAccount, currentUser: IUser) => {
    if (!currentAccount) return fromJS({});
    const { contactFirstname, contactLastname, contactAvatar, contactEmail, contactPhone, contactRole } =
      currentAccount;

    return fromJS({
      contactName: `${contactFirstname} ${contactLastname}`,
      contactAvatar,
      contactEmail,
      contactPhone,
      contactRole,
      myAccount: currentUser && currentAccount.mainUserId === currentUser.id,
    });
  },
);

export const getAvailableUsers = createSelector(
  findAllValues('user'),
  (users: IModelsObject) => {
    if (!users) return List([]);
    return List(users) as List<IUser>;
  }
);

export const getAccountPublishedEmail = createSelector(getTypedAccount, (account: IAccount) => {
  return account ? account.publishedEmail || '' : '';
});

export const getAccountContactString = createSelector(getTypedAccount, (account: IAccount) => {
  if (!account) return '';
  const contactEmail = account.contactEmail;
  const contactPhone = account.contactPhone;

  let address = '';

  contactEmail && (address += `\nemail: ${contactEmail}`);
  contactPhone && (address += `\nphone: ${contactPhone}`);

  return address;
});